import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isABVariant, isDev, getEstimatedDuration } from '../../utils/utils';
import { setFixerId } from '../../actions/course';
import * as api from '../../utils/api';
// Hooks
import useAlert from '../../hooks/useModal';
// Components
import Box from '../shared/atoms/Box';
// Icons
import LightBulbColor32 from '../icons/LightBulbColor32';
// Utils
import daysWithOptiv from './utils/daysWithOptiv';
import SelectedSlots from './SelectedSlots';
import HelpBox from './HelpBox';
import DateTimeModuleSkeleton from './DateTimeModuleSkeleton';
import MoreSlotsModal from '../../modals/MoreSlotsModal';
import Link from '../shared/atoms/Link';
import Separator from '../Separator';
import useEstimatedDuration from '../../hooks/useEstimatedDuration';
import addSlotsBonus, { yieldBonusType } from '../../utils/slots';
import { useCurrentZone } from '../../hooks/useZones';
import Button from '../shared/atoms/Button';
import Text from '../shared/atoms/Text';
import CompactDayCard from './CompactDayCard';
import ExpandableDayCard from './ExpandableDayCard';
import DaySlotBottomSheet from './DaySlotBottomSheet';

const DateTimeModule = ({
  fixed,
  slots,
  toggleSlot,
  position,
  product_id,
  fixerId,
  shopId,
  noDispoModalOpen,
  toContinue = true,
  capabilities,
  onSubmit,
  bookingUuid,
  trackFunnel = false,
}) => {
  const { t } = useTranslation();
  const estimated_duration = useEstimatedDuration();

  const [days, setDays] = useState([]);
  const [basicDays, setBasicDays] = useState([]);
  const [averageOptiv, setAverageOptiv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moreSlots, setMoreSlots] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showAllDays, setShowAllDays] = useState(false);
  const [selectedDayId, setSelectedDayId] = useState(null);
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [zeroSlotWithFixer, setZeroSlotWithFixer] = useState(false);
  const available_min = false;
  const available_max = false;
  const history = useHistory();
  const showModal = useAlert();
  const zone = useCurrentZone();
  const dispatch = useDispatch();
  const sourceVisitReportId = useSelector(
    (s) => s.course.source_visit_report_id,
  );
  const visitReportData = useSelector((s) => s.course.visit_report_data);
  const fixerName =
    visitReportData &&
    visitReportData.course &&
    visitReportData.course.fixer &&
    visitReportData.course.fixer.prenom;
  const showDevisChip = !!(fixerId && sourceVisitReportId && fixerName);

  // FIX-579: instrument the slot-selection/validation step so drop-off here
  // can be measured (previously no event existed for this step at all —
  // only a generic success-side `availabilities` GTM push). Fire-and-forget,
  // same "never block the user" contract as the other funnel `analytics/track`
  // calls (RepairConfirmation.jsx, RepairCheckoutWithPayment.jsx).
  //
  // `step` must be one of AnalyticsEvent::VALID_STEPS on the api side — an
  // unknown one comes back 400 and the catch below eats it, so the event is
  // lost with no signal at all. `availabilities_viewed` is rung 2 of the
  // funnel there; `slot_validation_failed` is a plain event (api#784).
  //
  // Opt-in, and off by default: this component is also mounted OUTSIDE the
  // booking funnel (UpdateDisponibility.jsx — rescheduling an existing
  // course from the customer area). Emitting rung 2 from there would inflate
  // both the step count and `total_funnels` in getFunnelStats(), i.e. corrupt
  // the very drop-off measurement FIX-579 exists to get.
  const trackFunnelStep = (step, context = {}) => {
    if (!trackFunnel) return;

    api.post('/analytics/track', { step, context }).catch(() => {
      // Silent fail - don't block the user experience
    });
  };

  useEffect(() => {
    trackFunnelStep('availabilities_viewed', {
      zone_id: zone?.id,
      is_devis_rebook: !!sourceVisitReportId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConfigs = async (position, product_id, fixerId) => {
    if (!position || !product_id) {
      setLoading(false);
      return;
    }

    const MAX_DAYS = isDev() ? 10 : 6;
    const dateFrom = moment().format('YYYY-MM-DD');
    const dateTo = moment()
      .add(MAX_DAYS - 1, 'day')
      .endOf('day')
      .format('YYYY-MM-DD');
    try {
      const opts = {
        position,
        date: { from: dateFrom, to: dateTo },
        max: MAX_DAYS,
        product_id,
        capabilities,
      };
      if (fixerId) {
        opts.fixer_id = fixerId;
      } else if (shopId) {
        opts.shop_id = shopId;
      }
      if (estimated_duration > 0) {
        opts.estimated_duration = estimated_duration;
      }
      const days = await api.get('/zones/days', opts);

      if (days?.error_message) {
        setLoading(false);
        trackFunnelStep('slot_validation_failed', {
          reason: 'days_fetch_error',
          zone_id: zone?.id,
        });
        history.push('/');
        showModal({
          title: t('error'),
          content: days?.error_message,
          showModal: true,
          icon: <LightBulbColor32 />,
          showButton: [
            {
              text: t('close.cta'),
              color: 'blue.electric',
            },
          ],
        });
        return;
      }

      // no availabilities in the next days
      if (!Object.keys(days).length) {
        setLoading(false);
        trackFunnelStep('slot_validation_failed', {
          reason: 'no_availability',
          zone_id: zone?.id,
        });
        // In the devis-rebook flow with a fixer filter active, do not open the
        // global NoDispo modal — surface an inline banner so the user can
        // disable the fixer filter without leaving the page.
        if (fixerId && sourceVisitReportId) {
          setZeroSlotWithFixer(true);
        } else {
          noDispoModalOpen(true);
        }
        return;
      }
      setZeroSlotWithFixer(false);

      const minDate = Object.keys(days)[0]; // select firstDate
      if (
        !isDev() &&
        typeof window !== 'undefined' &&
        typeof window.dataLayer !== 'undefined'
      )
        window.dataLayer.push({
          event: 'availabilities',
          daydiff:
            moment(minDate)
              .startOf('day')
              .diff(moment().startOf('day'), 'days') + 1,
        });

      const HOUR_MIN = 7;
      const HOUR_MAX = 22;

      const newDays = {};

      // converts the openv vector to user-friendly time slots
      Object.keys(days)
        .slice(0, 6)
        .forEach((dID) => {
          const day = days[dID];
          day.slots = [];
          // loop on the 96 items
          for (let idx = 0; idx < day.openv.length; idx++) {
            const min = Math.floor(idx / 4);
            const max = Math.floor(idx / 4) + 1;

            if (min < HOUR_MIN || max > HOUR_MAX) continue;
            // check every quarter of an hour
            const quarterOfHourFree = {
              0: day.openv[4 * min],
              15: day.openv[4 * min + 1],
              30: day.openv[4 * min + 2],
              45: day.openv[4 * min + 3],
            };
            // Check if 1 in quarters
            if (day.openv[idx] > 0) {
              day.slots.push({
                min,
                max,
                ...quarterOfHourFree,
                full: false,
              });
              // add the missing quarters of an hour to spend a next hour
              idx = Math.floor(idx / 4) * 4 + 3;
            } else if ((idx + 1) % 4 === 0) {
              // a 0 in a 4th item ("Case") means the slot is full
              day.slots.push({
                min,
                max,
                ...quarterOfHourFree,
                full: true,
              });
            }
          }

          newDays[dID] = day;
        });

      // looks for slots in global state that are non longer available
      const deletableSlots = slots.filter((slot) => {
        const momentSlot = moment(slot.available_min);
        const daySlot = momentSlot.format('YYYY-MM-DD');
        const hourSlot = parseInt(momentSlot.format('H'));

        if (!newDays.hasOwnProperty(daySlot)) return true;
        return !newDays[daySlot].slots.some(
          (newDaysSlot) => newDaysSlot.min === hourSlot && !newDaysSlot.full,
        );
      });

      deletableSlots.forEach((deletableSlot) =>
        toggleSlot(deletableSlot.available_min, deletableSlot.available_max),
      );
      setBasicDays(newDays);
      setLoading(false);
    } catch (e) {
      trackFunnelStep('slot_validation_failed', {
        reason: 'days_fetch_error',
        zone_id: zone?.id,
      });
      history.push('/');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs(position, product_id, fixerId);
  }, [position, product_id, fixerId]);

  useEffect(() => {
    daysWithOptiv({ setAverageOptiv, setDays, basicDays });
  }, [basicDays]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const openModal = ({ title, content, type = 'default' }) => {
    if (type === 'slots') {
      setMoreSlots(true);
      return;
    }
    showModal({
      title,
      content,
      showModal: true,
      icon: <LightBulbColor32 />,
      showButton: [
        {
          text: t('close.cta'),
          color: 'blue.electric',
        },
      ],
    });
  };

  const submit = (e) => {
    e.preventDefault();

    // FIX-579: fires on every "Continuer" click, success or failure — the
    // denominator for how often slot_validation_failed actually represents.
    trackFunnelStep('slot_validation_attempted', {
      zone_id: zone?.id,
      slots_count: slots.length,
    });

    if (slots.length === 0) {
      trackFunnelStep('slot_validation_failed', {
        reason: 'no_slot_selected',
        zone_id: zone?.id,
      });
      showModal({
        title: t('selectslots'),
        content: t(`getanappointmentfaster`, { label: 5 - slots.length }),
        showModal: true,
        showButton: [
          {
            text: t('addslots'),
            width: '200px',
          },
        ],
      });
    } else if (
      slots.some((slot) => !!yieldBonusType(slot.available_min)) &&
      zone?.id == 1
    ) {
      trackFunnelStep('slot_validation_failed', {
        reason: 'yield_warning',
        zone_id: zone?.id,
      });
      showModal({
        title: t('yield.warning.head'),
        content: (
          <div>
            {t(`yield.warning`)}
            <br />
            {t(`yield.warning2`)}
          </div>
        ),
        showModal: true,
        showButton: [
          {
            text: t('modify.cta'),
            variant: 'neutral',
            width: '200px',
            color: 'black',
            datatestid: 'button-modal-valid-slots',
          },
          {
            text: t('continue'),
            width: '200px',
            onClick: () =>
              onSubmit({
                date: false,
                available_min,
                available_max,
              }),
          },
        ],
      });
    } else if (slots.length < 5 && !isABVariant() && !shopId) {
      trackFunnelStep('slot_validation_failed', {
        reason: 'min_slots_prompted',
        zone_id: zone?.id,
        slots_count: slots.length,
      });
      showModal({
        title: t('doyouhavemoredisponibility'),
        content: t(`getanappointmentfaster`, { label: 5 - slots.length }),
        showModal: true,
        showButton: [
          {
            text: t('continue'),
            variant: 'neutral',
            width: '200px',
            color: 'black',
            datatestid: 'button-modal-valid-slots',
            onClick: () => {
              trackFunnelStep('slot_validation_failed', {
                reason: 'min_slots_continue',
                zone_id: zone?.id,
                slots_count: slots.length,
              });
              onSubmit({
                date: false,
                available_min,
                available_max,
              });
            },
          },
          {
            text: t('addslots'),
            width: '200px',
            datatestid: 'button-modal-add-slots',
            onClick: () =>
              trackFunnelStep('slot_validation_failed', {
                reason: 'min_slots_add_clicked',
                zone_id: zone?.id,
                slots_count: slots.length,
              }),
          },
        ],
      });
    } else {
      onSubmit({
        date: false,
        available_min,
        available_max,
      });
    }
  };

  const fullSelect = (dayID, slots, value) => {
    const day = moment(moment(days[dayID].date));
    const restartCheck = slots.filter((slot) => !slot.checked && !slot.full);

    slots.forEach((slot) => {
      const isChecked = value || !slot.checked;
      if (isChecked && !slot.full && !!restartCheck)
        toggleSlot(
          day.set({ hour: slot.min }).format('YYYY-MM-DD HH:00:00'),
          day.set({ hour: slot.max }).format('YYYY-MM-DD HH:00:00'),
        );
    });
  };

  return (
    <div>
      <MoreSlotsModal
        open={moreSlots}
        close={() => setMoreSlots(false)}
        days={days}
        bookingUuid={bookingUuid}
      />
      {showDevisChip && (
        <Box
          mb={4}
          p={3}
          bg="primary.lightest"
          borderRadius="sm"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          data-testid="datetime-fixer-chip">
          <Text mb={0} mr={3} fontWeight={2}>
            {t('datetimemodule.fixerchip.label', { name: fixerName })}
          </Text>
          <Link
            color="blue.electric"
            onClick={() => dispatch(setFixerId(null))}
            data-testid="datetime-fixer-toggle">
            {t('datetimemodule.fixerchip.seeall')}
          </Link>
        </Box>
      )}
      {zeroSlotWithFixer && showDevisChip && (
        <Box
          bg="primary.lightest"
          p={4}
          borderRadius="sm"
          mb={4}
          data-testid="datetime-zeroslot-banner">
          <Text mb={2}>
            {t('datetimemodule.zeroslot.banner.text', { name: fixerName })}
          </Text>
          <Button
            size="compact"
            onClick={() => {
              dispatch(setFixerId(null));
              setZeroSlotWithFixer(false);
            }}>
            {t('datetimemodule.zeroslot.banner.cta')}
          </Button>
        </Box>
      )}
      {loading && <DateTimeModuleSkeleton />}
      {!!days && !loading && isMobile && (
        <Box>
          {Object.keys(days).map((dayID) => {
            const day = days[dayID];
            const dayMoment = moment(day.date);
            let checkedSlots = day.slots.map((slot) => {
              const checked = slots.find(
                (s) =>
                  s.available_min ===
                    dayMoment
                      .set({ hour: slot.min })
                      .format('YYYY-MM-DD HH:00:00') &&
                  s.available_max ===
                    dayMoment
                      .set({ hour: slot.max })
                      .format('YYYY-MM-DD HH:00:00'),
              );
              return { ...slot, checked: !!checked };
            });
            if (zone?.id == 1) {
              checkedSlots = addSlotsBonus(dayMoment, checkedSlots);
            }
            const selectedCount = checkedSlots.filter((s) => s.checked).length;

            return (
              <CompactDayCard
                key={dayID}
                day={day}
                slots={checkedSlots}
                selectedCount={selectedCount}
                onClick={() => {
                  setSelectedDayId(dayID);
                  setBottomSheetOpen(true);
                }}
              />
            );
          })}
        </Box>
      )}
      {!!days && !loading && !isMobile && (
        <Box>
          {Object.keys(days).map((dayID) => {
            const day = days[dayID];
            const dayMoment = moment(day.date);
            let checkedSlots = day.slots.map((slot) => {
              const checked = slots.find(
                (s) =>
                  s.available_min ===
                    dayMoment
                      .set({ hour: slot.min })
                      .format('YYYY-MM-DD HH:00:00') &&
                  s.available_max ===
                    dayMoment
                      .set({ hour: slot.max })
                      .format('YYYY-MM-DD HH:00:00'),
              );
              return { ...slot, checked: !!checked };
            });
            if (zone?.id == 1) {
              checkedSlots = addSlotsBonus(dayMoment, checkedSlots);
            }
            const selectedCount = checkedSlots.filter((s) => s.checked).length;

            return (
              <ExpandableDayCard
                key={dayID}
                day={day}
                slots={checkedSlots}
                selectedCount={selectedCount}
                toggleSlot={toggleSlot}
                averageOptiv={averageOptiv}
              />
            );
          })}
        </Box>
      )}
      {!shopId && !isMobile && <HelpBox {...{ openModal }} />}
      <DaySlotBottomSheet
        open={bottomSheetOpen}
        onClose={() => setBottomSheetOpen(false)}
        day={selectedDayId ? days[selectedDayId] : null}
        slots={
          selectedDayId && days[selectedDayId]
            ? (() => {
                const dayMoment = moment(days[selectedDayId].date);
                let checkedSlots = days[selectedDayId].slots.map((slot) => {
                  const checked = slots.find(
                    (s) =>
                      s.available_min ===
                        dayMoment
                          .set({ hour: slot.min })
                          .format('YYYY-MM-DD HH:00:00') &&
                      s.available_max ===
                        dayMoment
                          .set({ hour: slot.max })
                          .format('YYYY-MM-DD HH:00:00'),
                  );
                  return { ...slot, checked: !!checked };
                });
                if (zone?.id == 1) {
                  checkedSlots = addSlotsBonus(dayMoment, checkedSlots);
                }
                return checkedSlots;
              })()
            : []
        }
        selectedSlots={slots}
        onToggleSlot={toggleSlot}
        zone={zone}
        trackFunnelStep={trackFunnelStep}
      />
      <Box display={{ xs: 'none', md: 'block' }}>
        <Link
          style={{ flex: 1 }}
          color="blue.electric"
          onClick={() => openModal({ type: 'slots' })}>
          {shopId ? t('datetimemodule.links.1') : t('datetimemodule.links.2')}
        </Link>
      </Box>
      <Separator axe="horizontal" mt={4} />
      {!loading && (
        <SelectedSlots
          {...{ slots, fixed, submit, toContinue, shopId, trackFunnelStep }}
          zoneId={zone?.id}
        />
      )}
    </div>
  );
};

DateTimeModule.propTypes = {
  position: PropTypes.string.isRequired,
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  available_min: PropTypes.string,
  available_max: PropTypes.string,
  selectDate: PropTypes.func,
  selectAvailable: PropTypes.func,
  product_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  capabilities: PropTypes.array,
  // Only the booking funnel sets this — see trackFunnelStep above.
  trackFunnel: PropTypes.bool,
};
export default DateTimeModule;
